import React, { useState, useEffect } from "react";
import { SearchResultRowRef } from "../SearchResultRow";

interface DetailedNutritionTableProps {
    dayIndex: number;
    dayData: any;
    searchResultRowRefs: React.MutableRefObject<Map<string, SearchResultRowRef>>;
    client: any;
}

// Global cache for detailed food data to avoid redundant API calls
const detailedFoodDataCache = new Map<string, any>();

export const DetailedNutritionTable: React.FC<DetailedNutritionTableProps> = ({
    dayIndex,
    dayData,
    searchResultRowRefs,
    client,
}) => {
    const [loading, setLoading] = useState(true);
    const [allNutrients, setAllNutrients] = useState<{ name: string; amount: number; unit: string }[]>([]);

    useEffect(() => {
        const fetchDetailedNutrition = async () => {
            if (!client) {
                setLoading(false);
                return;
            }

            setLoading(true);
            const nutrientTotals = new Map<string, { amount: number; unit: string }>();

            try {
                // Iterate through all meals in the day
                const mealTypes = ["breakfast", "lunch", "dinner", "snack"] as const;
                
                for (const mealType of mealTypes) {
                    const mealSlot = dayData[mealType] as { recipes: any[] };
                    if (!mealSlot?.recipes) continue;

                    for (let recipeIndex = 0; recipeIndex < mealSlot.recipes.length; recipeIndex++) {
                        const recipe = mealSlot.recipes[recipeIndex];
                        if (!recipe.ingredients) continue;

                        for (let ingredientIndex = 0; ingredientIndex < recipe.ingredients.length; ingredientIndex++) {
                            const refKey = `${dayIndex}-${mealType}-${recipeIndex}-${ingredientIndex}`;
                            const rowRef = searchResultRowRefs.current.get(refKey);
                            
                            if (rowRef) {
                                // Get the ingredient with current edited quantity
                                const ingredient = rowRef.getResultWithStateQuantity();
                                
                                if (ingredient.refCode) {
                                    // Check cache first
                                    let detailedFood = detailedFoodDataCache.get(ingredient.refCode);
                                    
                                    if (!detailedFood) {
                                        // Fetch detailed food data
                                        try {
                                            const response = await client.getFoodByRefCode(ingredient.refCode);
                                            if (response?.results?.[0]?.ingredients?.[0]) {
                                                detailedFood = response.results[0].ingredients[0];
                                                detailedFoodDataCache.set(ingredient.refCode, detailedFood);
                                            }
                                        } catch (error) {
                                            console.error(`Failed to fetch details for ${ingredient.refCode}:`, error);
                                            continue;
                                        }
                                    }
                                    
                                    if (detailedFood?.nutrients && ingredient.nutritionPreview?.portion) {
                                        const portion = ingredient.nutritionPreview.portion;
                                        const effectiveQuantity = portion.suggestedQuantity?.[0] || portion.quantity || 1;
                                        const totalWeight = effectiveQuantity * portion.weight.value; // in grams
                                        const scaleFactor = totalWeight / 100; // nutrients are per 100g
                                        
                                        // Add nutrients from this ingredient
                                        for (const nutrient of detailedFood.nutrients) {
                                            if (nutrient.nutrient?.name && nutrient.amount !== undefined) {
                                                const name = nutrient.nutrient.name;
                                                const amount = nutrient.amount * scaleFactor;
                                                const unit = nutrient.nutrient.unit || "";
                                                
                                                if (nutrientTotals.has(name)) {
                                                    const existing = nutrientTotals.get(name)!;
                                                    existing.amount += amount;
                                                } else {
                                                    nutrientTotals.set(name, { amount, unit });
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }

                // Convert to array and sort
                const sortedNutrients = Array.from(nutrientTotals.entries())
                    .map(([name, data]) => ({
                        name,
                        amount: data.amount,
                        unit: data.unit,
                    }))
                    .sort((a, b) => {
                        // Put items starting with numbers at the end
                        const aStartsWithNumber = /^\d/.test(a.name);
                        const bStartsWithNumber = /^\d/.test(b.name);
                        
                        if (aStartsWithNumber && !bStartsWithNumber) return 1;
                        if (!aStartsWithNumber && bStartsWithNumber) return -1;
                        
                        return a.name.localeCompare(b.name);
                    });

                setAllNutrients(sortedNutrients);
            } catch (error) {
                console.error("Error calculating detailed nutrition:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchDetailedNutrition();
    }, [dayIndex, dayData, searchResultRowRefs, client]);

    // Helper to format nutrient names
    const formatNutrientName = (name: string) => {
        return name
            .split(/[_\s]+/)
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(" ");
    };

    // Helper to format nutrient value with unit
    const formatNutrientValue = (amount: number, unit: string) => {
        const rounded = Math.round(amount * 100) / 100;
        return { value: rounded, unit };
    };

    const containerStyle: React.CSSProperties = {
        marginTop: "16px",
        backgroundColor: "white",
        borderRadius: "8px",
        border: "1px solid #e5e7eb",
        padding: "20px",
    };

    const titleStyle: React.CSSProperties = {
        fontSize: "16px",
        fontWeight: "600",
        color: "#374151",
        marginBottom: "16px",
        paddingBottom: "8px",
        borderBottom: "2px solid #e5e7eb",
    };

    const nutrientItemStyle: React.CSSProperties = {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "10px 24px",
        borderBottom: "1px solid #e5e7eb",
        backgroundColor: "#f9fafb",
    };

    const loadingStyle: React.CSSProperties = {
        textAlign: "center",
        padding: "40px",
        color: "#6b7280",
    };

    if (loading) {
        return (
            <div style={containerStyle}>
                <div style={loadingStyle}>
                    <div style={{
                        display: "inline-block",
                        width: "40px",
                        height: "40px",
                        border: "3px solid #f3f4f6",
                        borderRadius: "50%",
                        borderTopColor: "#3b82f6",
                        animation: "spin 1s linear infinite",
                        marginBottom: "12px",
                    }} />
                    <div>Loading detailed nutrition data...</div>
                </div>
            </div>
        );
    }

    if (allNutrients.length === 0) {
        return (
            <div style={containerStyle}>
                <div style={titleStyle}>All Nutrients - Day {dayIndex + 1}</div>
                <div style={{ textAlign: "center", padding: "20px", color: "#6b7280" }}>
                    No detailed nutrition data available. Add ingredients with nutrition information to see details.
                </div>
            </div>
        );
    }

    // Split nutrients into two columns
    const midpoint = Math.ceil(allNutrients.length / 2);
    const leftColumn = allNutrients.slice(0, midpoint);
    const rightColumn = allNutrients.slice(midpoint);

    const columnContainerStyle: React.CSSProperties = {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "20px",
        maxHeight: "400px",
        overflowY: "auto",
        padding: "0",
    };

    const columnStyle: React.CSSProperties = {
        border: "1px solid #e5e7eb",
        borderRadius: "8px",
        backgroundColor: "#f9fafb",
        overflow: "hidden",
    };

    const renderNutrientItem = (nutrient: { name: string; amount: number; unit: string }, index: number, isLastInColumn: boolean) => {
        const formatted = formatNutrientValue(nutrient.amount, nutrient.unit);
        const isZero = formatted.value === 0;
        
        return (
            <div
                key={`${nutrient.name}-${index}`}
                style={{
                    ...nutrientItemStyle,
                    borderBottom: isLastInColumn ? "none" : "1px solid #e5e7eb",
                    opacity: isZero ? 0.5 : 1,
                    padding: "8px 16px",
                }}
            >
                <span style={{ 
                    color: isZero ? "#9ca3af" : "#374151", 
                    fontSize: "13px",
                    transition: "color 0.2s",
                    flex: 1,
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                }}>
                    {formatNutrientName(nutrient.name)}
                </span>
                <div style={{ 
                    display: "flex", 
                    alignItems: "baseline", 
                    gap: "3px",
                    flexShrink: 0,
                }}>
                    <span style={{ 
                        color: isZero ? "#9ca3af" : "#1f2937", 
                        fontSize: "13px", 
                        fontWeight: "600",
                    }}>
                        {formatted.value}
                    </span>
                    <span style={{ 
                        color: isZero ? "#d1d5db" : "#6b7280", 
                        fontSize: "11px",
                        fontWeight: "400",
                    }}>
                        {formatted.unit}
                    </span>
                </div>
            </div>
        );
    };

    return (
        <>
            <style>
                {`
                    .nutrition-columns-container {
                        scrollbar-width: thin;
                        scrollbar-color: #cbd5e0 #f3f4f6;
                    }
                    .nutrition-columns-container::-webkit-scrollbar {
                        width: 8px;
                        height: 8px;
                    }
                    .nutrition-columns-container::-webkit-scrollbar-track {
                        background: #f3f4f6;
                        border-radius: 4px;
                    }
                    .nutrition-columns-container::-webkit-scrollbar-thumb {
                        background: #cbd5e0;
                        border-radius: 4px;
                    }
                    .nutrition-columns-container::-webkit-scrollbar-thumb:hover {
                        background: #9ca3af;
                    }
                `}
            </style>
            <div style={containerStyle}>
                <div style={titleStyle}>
                    All Nutrients - Day {dayIndex + 1} ({allNutrients.length} nutrients tracked)
                </div>
                <div style={columnContainerStyle} className="nutrition-columns-container">
                    <div style={columnStyle}>
                        {leftColumn.map((nutrient, index) => 
                            renderNutrientItem(nutrient, index, index === leftColumn.length - 1)
                        )}
                    </div>
                    <div style={columnStyle}>
                        {rightColumn.map((nutrient, index) => 
                            renderNutrientItem(nutrient, index, index === rightColumn.length - 1)
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}; 